import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  reload,
} from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import API from "../api";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  /* ================= ACCOUNT FORM ================= */
  const [account, setAccount] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });

  /* ================= ORGANIZATION FORM ================= */
  const [org, setOrg] = useState({
    basic: {
      companyName: "",
      companySize: "",
      businessType: "",
    },
    contact: {
      countryCode: "+91",
      phone: "",
      officialEmail: "",
    },
    legal: {
      gstNumber: "",
      panNumber: "",
      tanNumber: "",
      cinNumber: "",
    },
    address: {
      country: "",
      state: "",
      district: "",
      city: "",
      pincode: "",
      fullAddress: "",
    },
  });

  /* ================= STEP 1 ================= */
  const handleCreateAccount = async () => {
    if (account.password !== account.confirmPassword)
      return toast.error("Passwords do not match");

    try {
      setLoading(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        account.email,
        account.password
      );

      await sendEmailVerification(userCredential.user);
      setFirebaseUser(userCredential.user);

      toast.success("Verification email sent!");
      setStep(2);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCheck = async () => {
    await reload(firebaseUser);
    if (firebaseUser.emailVerified) {
      toast.success("Email verified successfully");
      setStep(3);
    } else {
      toast.error("Email not verified yet");
    }
  };

  /* ================= STEP 2 ================= */
const handleCreateOrganization = async () => {
  try {
    setLoading(true);
localStorage.setItem("creatingOrg", "true");
    if (!firebaseUser) {
      toast.error("Authentication error. Please login again.");
      return;
    }

    // 🔥 STEP 1: Get token
    let token = await firebaseUser.getIdToken();

    // 🔥 STEP 2: Create org
    const res = await API.post(
      "create-organization/",
      org,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // 🔥 STEP 3: FORCE REFRESH TOKEN (VERY IMPORTANT)
    token = await firebaseUser.getIdToken(true);

    // 🔥 STEP 4: VERIFY AGAIN (GET UPDATED ORG DATA)
    const verifyRes = await API.post(
      "verify-token/",
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const orgs = verifyRes.data.organizations || [];

    if (orgs.length === 0) {
      toast.error("Organization sync failed. Please login again.");
      return;
    }

    const orgData = orgs[0]; // newly created org

    // 🔥 STEP 5: STORE CORRECT DATA
    localStorage.setItem("orgId", orgData.orgId);
    localStorage.setItem("orgSlug", orgData.orgSlug);
    localStorage.setItem("role", orgData.role);

    toast.success("Organization Created Successfully 🚀");

setTimeout(() => {
  localStorage.removeItem("creatingOrg");

  navigate(`/${orgData.orgSlug}/admin-dashboard`);
}, 1200);
  } catch (err) {
    toast.error(err.response?.data?.error || err.message);
  } finally {
    setLoading(false);
  }
};

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6">
      <Toaster />

      <div className="w-full max-w-4xl bg-white shadow-2xl rounded-2xl p-10">

        {/* HEADER */}
        <h2 className="text-3xl font-bold text-slate-800 text-center mb-6">
          Corporate Organization Onboarding
        </h2>

        {/* ================= STEP 1 ================= */}
        {step === 1 && (
          <div className="space-y-5">
            <input
              type="email"
              placeholder="Business Email"
              className="input"
              onChange={(e) =>
                setAccount({ ...account, email: e.target.value })
              }
            />

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="input pr-12"
                onChange={(e) =>
                  setAccount({ ...account, password: e.target.value })
                }
              />
              <div
                className="absolute right-4 top-3 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </div>
            </div>

            <input
              type="password"
              placeholder="Confirm Password"
              className="input"
              onChange={(e) =>
                setAccount({ ...account, confirmPassword: e.target.value })
              }
            />

            <button onClick={handleCreateAccount} className="btn">
              {loading ? "Processing..." : "Create Account"}
            </button>
          </div>
        )}

        {/* ================= VERIFY ================= */}
        {step === 2 && (
          <div className="text-center">
            <p className="mb-6 text-slate-600">
              Please verify your email to continue.
            </p>
            <button onClick={handleVerifyCheck} className="btn">
              I Have Verified
            </button>
          </div>
        )}

        {/* ================= ORGANIZATION DETAILS ================= */}
        {step === 3 && (
          <div className="space-y-10">

            {/* BASIC */}
            <Section title="Basic Details">
              <input className="input" placeholder="Company Name"
                onChange={(e)=>setOrg({...org,basic:{...org.basic,companyName:e.target.value}})} />

              <input className="input" placeholder="Company Size"
                onChange={(e)=>setOrg({...org,basic:{...org.basic,companySize:e.target.value}})} />
     

              <input className="input" placeholder="Business Type"
                onChange={(e)=>setOrg({...org,basic:{...org.basic,businessType:e.target.value}})} />
            </Section>

            {/* CONTACT */}
            <Section title="Contact Details">
              <div className="flex gap-4">
                <input className="input w-1/4" placeholder="+91"
                  onChange={(e)=>setOrg({...org,contact:{...org.contact,countryCode:e.target.value}})} />
                <input className="input w-3/4" placeholder="Phone Number"
                  onChange={(e)=>setOrg({...org,contact:{...org.contact,phone:e.target.value}})} />
              </div>

              <input className="input" placeholder="Official Email"
                onChange={(e)=>setOrg({...org,contact:{...org.contact,officialEmail:e.target.value}})} />
            </Section>

            {/* LEGAL (OPTIONAL) */}
            <Section title="Legal Details (Optional)">
              <input className="input" placeholder="GST Number"
                onChange={(e)=>setOrg({...org,legal:{...org.legal,gstNumber:e.target.value}})} />

              <input className="input" placeholder="PAN Number"
                onChange={(e)=>setOrg({...org,legal:{...org.legal,panNumber:e.target.value}})} />

              <input className="input" placeholder="TAN Number"
                onChange={(e)=>setOrg({...org,legal:{...org.legal,tanNumber:e.target.value}})} />

              <input className="input" placeholder="CIN Number"
                onChange={(e)=>setOrg({...org,legal:{...org.legal,cinNumber:e.target.value}})} />
            </Section>

            {/* ADDRESS */}
            <Section title="Address">
              <input className="input" placeholder="Country"
                onChange={(e)=>setOrg({...org,address:{...org.address,country:e.target.value}})} />
              <input className="input" placeholder="State"
                onChange={(e)=>setOrg({...org,address:{...org.address,state:e.target.value}})} />
              <input className="input" placeholder="District"
                onChange={(e)=>setOrg({...org,address:{...org.address,district:e.target.value}})} />
              <input className="input" placeholder="City"
                onChange={(e)=>setOrg({...org,address:{...org.address,city:e.target.value}})} />
              <input className="input" placeholder="Pincode"
                onChange={(e)=>setOrg({...org,address:{...org.address,pincode:e.target.value}})} />
              <textarea className="input" placeholder="Full Address"
                onChange={(e)=>setOrg({...org,address:{...org.address,fullAddress:e.target.value}})} />
            </Section>

            <button onClick={handleCreateOrganization} className="btn">
              {loading ? "Creating..." : "Create Organization 🚀"}
            </button>

          </div>
        )}
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 12px 14px;
          border-radius: 10px;
          border: 1px solid #d1d5db;
          margin-bottom: 12px;
          outline: none;
        }
        .input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 2px rgba(99,102,241,0.2);
        }
        .btn {
          width: 100%;
          padding: 12px;
          background: #4f46e5;
          color: white;
          border-radius: 10px;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

/* ================= SECTION COMPONENT ================= */
function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-700 mb-4 border-b pb-2">
        {title}
      </h3>
      {children}
    </div>
  );
}